import React from "react";
import SeoHead from "@/components/SeoHead";
import { AlertTriangle, ArrowUpRight, Building2, HeartHandshake, TrendingUp, Users } from "lucide-react";

/* /demo/organization — the B2B surface, shown with a fictional network so
 * investors see how organizations plug in without a second login. */

const FAMILIES = [
    { name: "Chen family", caregiver: "Sarah C.", engagement: "Daily", risk: "Stable", last: "Today" },
    { name: "Okafor family", caregiver: "David O.", engagement: "4x / week", risk: "Stable", last: "Yesterday" },
    { name: "Reyes family", caregiver: "Lucia R.", engagement: "2x / week", risk: "Watch — sleep disruption", last: "3 days ago" },
    { name: "Brandt family", caregiver: "Tom B.", engagement: "Dropped off", risk: "Outreach suggested", last: "9 days ago" },
];

export default function DemoOrganization() {
    return (
        <main className="min-h-screen bg-cream pt-10">
            <SeoHead title="Organization View — Memory Lane Demo" description="How care networks and clinics see their families in Memory Lane." canonicalPath="/investor-demo/organization" />
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
                <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-2 flex items-center gap-1.5">
                    <Building2 size={13} /> Organization view — fictional network
                </p>
                <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-[#12776F]" data-testid="demo-org-title">
                    Riverbend Memory Care Network
                </h1>
                <p className="mt-2 text-sm text-[#6E7A74]">Co-branded Memory Lane · 128 enrolled families · 3 locations</p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-7">
                    {[
                        [Users, "Enrolled families", "128", "+12 this month"],
                        [TrendingUp, "Weekly active caregivers", "61%", "vs 22% industry email engagement"],
                        [HeartHandshake, "Check-ins this week", "402", "avg 3.1 per active family"],
                        [AlertTriangle, "Families flagged for outreach", "6", "sleep, falls, burnout signals"],
                    ].map(([Icon, label, value, sub]) => (
                        <div key={label} className="ml-card p-4" data-testid={`demo-org-stat-${label.split(" ")[0].toLowerCase()}`}>
                            <Icon size={16} className="text-teal-dark" />
                            <p className="text-2xl font-heading font-semibold text-[#1F2624] mt-2">{value}</p>
                            <p className="text-xs font-medium text-[#4C5551]">{label}</p>
                            <p className="text-[11px] text-[#6E7A74] mt-1">{sub}</p>
                        </div>
                    ))}
                </div>

                <div className="ml-card mt-6 overflow-hidden" data-testid="demo-org-families">
                    <div className="px-5 py-3.5 border-b border-cream-dark flex items-center justify-between">
                        <p className="font-heading font-semibold text-[#1F2624] text-sm">Families — engagement &amp; early-warning signals</p>
                        <span className="text-[11px] text-[#6E7A74]">Consent-based · caregivers control what's shared</span>
                    </div>
                    <div className="divide-y divide-cream-dark">
                        {FAMILIES.map(f => (
                            <div key={f.name} className="px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                                <div>
                                    <p className="font-medium text-[#1F2624]">{f.name}</p>
                                    <p className="text-[11px] text-[#6E7A74]">{f.caregiver}</p>
                                </div>
                                <p className="text-[#4C5551] self-center">{f.engagement}</p>
                                <p className={`self-center text-[13px] ${f.risk.startsWith("Stable") ? "text-teal-dark" : "text-[#B7791F]"}`}>{f.risk}</p>
                                <p className="text-[#6E7A74] self-center text-[12px]">Last active: {f.last}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-8 rounded-3xl border border-teal/30 bg-teal-soft/40 p-7" data-testid="demo-org-ecosystem">
                    <h2 className="font-heading text-xl font-semibold text-[#12776F]">Why the whole healthcare ecosystem wins</h2>
                    <ul className="mt-3 space-y-2 text-[14px] text-[#2A3330] leading-relaxed">
                        <li>• <strong>Families</strong> get one living record, 24/7 guidance, and caregiver wellbeing support — 11M+ unpaid US dementia caregivers today, doubling by 2050.</li>
                        <li>• <strong>Clinicians</strong> get accurate histories and pre-visit briefs in a 15-minute-visit world — better decisions, less charting archaeology.</li>
                        <li>• <strong>Care organizations</strong> get engagement, early-warning signals, and differentiation — plus audit-grade consent they can put in front of compliance.</li>
                        <li>• <strong>Health systems &amp; payers</strong> get what actually bends cost: fewer avoidable ER visits, delayed institutionalization, and CMS's GUIDE Model gives this a reimbursement rail.</li>
                        <li>• <strong>The person living with dementia</strong> gets the quiet outcome all of it serves: more good days at home, cared for by people who aren't running on empty.</li>
                    </ul>
                    <p className="mt-4 text-[13px] text-[#4C5551]">Dementia costs the US ~$350B a year and the coordination layer between home and healthcare simply doesn't exist. Memory Lane is that layer — owned by the family, valuable to every player around them.</p>
                </div>

                <div className="mt-6 rounded-3xl border border-teal/30 bg-white p-7" data-testid="demo-org-future">
                    <h2 className="font-heading text-xl font-semibold text-[#12776F] flex items-center gap-2">
                        Where this goes next <ArrowUpRight size={17} />
                    </h2>
                    <ul className="mt-3 space-y-2 text-[14px] text-[#2A3330] leading-relaxed">
                        <li>• <strong>Predictive signals:</strong> check-in + behavior data flags UTIs, falls risk, and caregiver burnout days before the crisis.</li>
                        <li>• <strong>Clinician portal:</strong> pre-visit briefs pushed straight into the EHR workflow.</li>
                        <li>• <strong>Payer &amp; health-system contracts:</strong> family engagement + fewer avoidable ER visits as reportable outcomes.</li>
                        <li>• <strong>A longitudinal record</strong> that follows the family across home care, memory care, and hospice.</li>
                    </ul>
                    <p className="mt-4 text-[13px] text-[#4C5551]">One caregiver-owned record, valuable to every layer of the care economy — that's the platform bet.</p>
                </div>
            </div>
        </main>
    );
}
